import React, { useEffect, useState } from 'react';
import apiClient from '../api';
import {
  Trophy, Crown, Award,
  Search, TrendingUp
} from 'lucide-react';

const LEVEL_TIERS = [
  { level: 1, name: 'Colaborador Inicial', minPoints: 1, minContrib: 1, color: 'from-teal-400 to-emerald-500', badge: '🌱' },
  { level: 2, name: 'Colaborador Activo', minPoints: 20, minContrib: 5, color: 'from-emerald-400 to-teal-600', badge: '🎵' },
  { level: 3, name: 'Colaborador Avanzado', minPoints: 50, minContrib: 10, color: 'from-blue-400 to-indigo-600', badge: '🎼' },
  { level: 4, name: 'Investigador Musical', minPoints: 100, minContrib: 20, color: 'from-indigo-400 to-purple-600', badge: '🔍' },
  { level: 5, name: 'Curador de la Memoria Sonora', minPoints: 250, minContrib: 50, color: 'from-purple-400 to-pink-600', badge: '🏛️' },
  { level: 6, name: 'Guardián del Patrimonio', minPoints: 500, minContrib: 100, color: 'from-amber-400 to-yellow-600', badge: '👑' },
];

const maskEmail = (email) => {
  if (!email) return '';
  const [name, domain] = email.split('@');
  if (!domain) return email;
  if (name.length <= 2) return `***@${domain}`;
  return `${name[0]}***${name[name.length - 1]}@${domain}`;
};

const getDisplayName = (entry) => {
  const full = `${entry.nombre || entry.student_first_name || ''} ${entry.apellido || entry.student_last_name || ''}`.trim();
  if (full) return full;
  return maskEmail(entry.email);
};

const getInitials = (entry) => {
  const name = getDisplayName(entry);
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || 'US').toUpperCase();
};

function GamificationPage() {
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(decoded.alumnoId);
      } catch (e) {
        // ignore
      }
    }

    const fetchRanking = async () => {
      try {
        const response = await apiClient.getRanking();
        setRanking(response.data || []);
      } catch (err) {
        console.error('Error fetching ranking:', err);
        setError('No se pudo cargar el ranking. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  const filteredRanking = ranking.filter((item) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const name = getDisplayName(item).toLowerCase();
    const email = (item.email || '').toLowerCase();
    const level = (item.level || '').toLowerCase();
    return name.includes(q) || email.includes(q) || level.includes(q);
  });

  const topThree = ranking.slice(0, 3);
  const myRankIndex = currentUserId ? ranking.findIndex((r) => r.id === currentUserId) : -1;
  const myRankEntry = myRankIndex >= 0 ? ranking[myRankIndex] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-10">

        {/* Encabezado Principal */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold">
            <Trophy size={15} />
            Sistema de Reconocimiento Académico y Colaborativo
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-amber-300 via-purple-300 to-pink-400 bg-clip-text text-transparent">
            Ranking y Salón de la Fama
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-base sm:text-lg">
            Suma puntos completando tareas en tus cátedras y contribuyendo al archivo histórico de compositores del Paraguay.
          </p>
        </div>

        {/* Banner de Mi Posición Actual (si el alumno está logueado) */}
        {myRankEntry && (
          <div className="bg-gradient-to-r from-purple-900/60 via-indigo-900/60 to-emerald-900/50 backdrop-blur-xl rounded-2xl border border-purple-400/40 p-5 sm:p-6 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-slate-950 font-black text-xl shadow-lg">
                #{myRankIndex + 1}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-purple-300 font-semibold">Tu Posición Actual</p>
                <h2 className="text-xl font-bold text-white">{getDisplayName(myRankEntry)}</h2>
                <p className="text-sm text-emerald-300 font-medium">{myRankEntry.level || 'Colaborador'}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <span className="block text-2xl font-extrabold text-amber-300">{myRankEntry.totalPuntos || 0}</span>
                <span className="text-xs text-slate-300">Puntos Totales</span>
              </div>
              <div className="text-center">
                <span className="block text-2xl font-extrabold text-purple-300">{myRankEntry.contributions || 0}</span>
                <span className="text-xs text-slate-300">Logros / Aportes</span>
              </div>
            </div>
          </div>
        )}

        {/* Podio Interactivo Top 3 */}
        {!loading && !error && topThree.length > 0 && (
          <div className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              {/* 2do Lugar (Plata) */}
              {topThree[1] && (
                <div className="order-2 md:order-1 bg-slate-900/70 backdrop-blur-xl rounded-2xl border border-slate-400/40 p-6 text-center relative hover:-translate-y-1 transition-all duration-300 shadow-xl">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-slate-300 text-slate-900 font-extrabold text-xs flex items-center gap-1 shadow">
                    🥈 2.º PUESTO
                  </div>
                  <div className="w-20 h-20 mx-auto mt-3 mb-4 rounded-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center text-slate-900 text-2xl font-extrabold ring-4 ring-slate-300/30">
                    {getInitials(topThree[1])}
                  </div>
                  <h3 className="text-lg font-bold text-white truncate">{getDisplayName(topThree[1])}</h3>
                  <p className="text-xs text-slate-400 mb-3">{maskEmail(topThree[1].email)}</p>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-slate-700/80 text-slate-200 border border-slate-500/40 mb-4">
                    {topThree[1].level || 'Colaborador'}
                  </span>
                  <div className="pt-3 border-t border-slate-800 flex justify-around text-sm">
                    <div>
                      <span className="block font-extrabold text-xl text-slate-200">{topThree[1].totalPuntos || 0}</span>
                      <span className="text-xs text-slate-400">Puntos</span>
                    </div>
                    <div>
                      <span className="block font-extrabold text-xl text-emerald-400">{topThree[1].contributions || 0}</span>
                      <span className="text-xs text-slate-400">Logros</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 1er Lugar (Oro - Centro Elevado) */}
              {topThree[0] && (
                <div className="order-1 md:order-2 bg-gradient-to-b from-amber-950/60 via-slate-900/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border-2 border-amber-400/60 p-7 text-center relative md:-translate-y-4 hover:-translate-y-5 transition-all duration-300 shadow-2xl shadow-amber-500/15">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 font-black text-xs flex items-center gap-1.5 shadow-lg">
                    <Crown size={15} /> 1.º CAMPEÓN
                  </div>
                  <div className="w-24 h-24 mx-auto mt-3 mb-4 rounded-full bg-gradient-to-br from-amber-300 via-yellow-500 to-orange-500 flex items-center justify-center text-slate-950 text-3xl font-black ring-4 ring-amber-400/40 shadow-lg">
                    {getInitials(topThree[0])}
                  </div>
                  <h3 className="text-xl font-extrabold text-white truncate">{getDisplayName(topThree[0])}</h3>
                  <p className="text-xs text-amber-200/70 mb-3">{maskEmail(topThree[0].email)}</p>
                  <span className="inline-block px-3.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-400/40 mb-4">
                    {topThree[0].level || 'Colaborador Destacado'}
                  </span>
                  <div className="pt-4 border-t border-amber-500/20 flex justify-around text-sm">
                    <div>
                      <span className="block font-black text-2xl text-amber-300">{topThree[0].totalPuntos || 0}</span>
                      <span className="text-xs text-slate-400">Puntos</span>
                    </div>
                    <div>
                      <span className="block font-black text-2xl text-emerald-400">{topThree[0].contributions || 0}</span>
                      <span className="text-xs text-slate-400">Logros</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 3er Lugar (Bronce) */}
              {topThree[2] && (
                <div className="order-3 bg-slate-900/70 backdrop-blur-xl rounded-2xl border border-amber-700/50 p-6 text-center relative hover:-translate-y-1 transition-all duration-300 shadow-xl">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-amber-700 text-amber-100 font-extrabold text-xs flex items-center gap-1 shadow">
                    🥉 3.º PUESTO
                  </div>
                  <div className="w-20 h-20 mx-auto mt-3 mb-4 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-white text-2xl font-extrabold ring-4 ring-amber-700/30">
                    {getInitials(topThree[2])}
                  </div>
                  <h3 className="text-lg font-bold text-white truncate">{getDisplayName(topThree[2])}</h3>
                  <p className="text-xs text-slate-400 mb-3">{maskEmail(topThree[2].email)}</p>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-amber-900/40 text-amber-300 border border-amber-700/40 mb-4">
                    {topThree[2].level || 'Colaborador'}
                  </span>
                  <div className="pt-3 border-t border-slate-800 flex justify-around text-sm">
                    <div>
                      <span className="block font-extrabold text-xl text-amber-400">{topThree[2].totalPuntos || 0}</span>
                      <span className="text-xs text-slate-400">Puntos</span>
                    </div>
                    <div>
                      <span className="block font-extrabold text-xl text-emerald-400">{topThree[2].contributions || 0}</span>
                      <span className="text-xs text-slate-400">Logros</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Guía Visual de Niveles e Insignias */}
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Award className="text-purple-400" size={24} />
                Niveles e Insignias de la Comunidad
              </h2>
              <p className="text-slate-400 text-sm">
                Escala de rangos según tus puntos académicos o aportes publicados en el Timeline
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LEVEL_TIERS.map((tier) => (
              <div
                key={tier.level}
                className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/60 hover:border-purple-500/40 transition-all flex items-center gap-4"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center text-2xl shadow-md flex-shrink-0`}>
                  {tier.badge}
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-300">
                    Nivel {tier.level}
                  </span>
                  <h3 className="text-white font-bold text-sm truncate">{tier.name}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {tier.minPoints}+ pts o {tier.minContrib}+ aporte(s)
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabla Completa de Clasificación */}
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <TrendingUp className="text-emerald-400" size={24} />
                Tabla General de Clasificación
              </h2>
              <p className="text-slate-400 text-sm">
                {ranking.length} participante(s) registrados en el ranking
              </p>
            </div>

            {/* Buscador */}
            <div className="relative w-full sm:w-72">
              <Search size={16} className="absolute left-3.5 top-3 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar alumno o nivel..."
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-800/80 border border-slate-700 text-sm text-white focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-300">Cargando ranking...</p>
            </div>
          )}

          {error && <p className="text-center text-red-400 py-8">{error}</p>}

          {!loading && !error && filteredRanking.length === 0 && (
            <p className="text-center text-slate-400 py-8">
              No se encontraron participantes con ese criterio de búsqueda.
            </p>
          )}

          {!loading && !error && filteredRanking.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-700/60">
                <thead className="bg-slate-800/80">
                  <tr>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Pos.</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Participante</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Rol / Cátedra</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-300 uppercase">Nivel</th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-slate-300 uppercase">Logros / Aportes</th>
                    <th className="px-4 py-3.5 text-right text-xs font-semibold text-slate-300 uppercase">Puntos Totales</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredRanking.map((contributor, idx) => {
                    const realRank = ranking.findIndex((r) => r.email === contributor.email) + 1;
                    const isMe = currentUserId && contributor.id === currentUserId;

                    return (
                      <tr
                        key={contributor.email || contributor.id || idx}
                        className={`hover:bg-slate-800/50 transition-colors ${isMe ? 'bg-purple-900/25 border-l-4 border-purple-400' : ''}`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-slate-200">
                          <div className="flex items-center gap-2">
                            <span>#{realRank}</span>
                            {realRank === 1 && <span title="Oro">🥇</span>}
                            {realRank === 2 && <span title="Plata">🥈</span>}
                            {realRank === 3 && <span title="Bronce">🥉</span>}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                              {getInitials(contributor)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-white flex items-center gap-2">
                                {getDisplayName(contributor)}
                                {isMe && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-purple-500/30 text-purple-200 border border-purple-400/40">
                                    Tú
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-slate-400">{maskEmail(contributor.email)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {contributor.is_student_contribution ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                Estudiante
                              </span>
                              {contributor.catedras?.length > 0 && (
                                <p className="text-xs text-slate-400 mt-1 truncate max-w-[200px]">
                                  {contributor.catedras.join(', ')}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                              Colaborador Web
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/15 text-purple-300 border border-purple-500/30">
                            {contributor.level || 'Colaborador Inicial'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-center font-semibold text-slate-200">
                          {contributor.contributions ?? contributor.actividadesCompletadas ?? 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-base font-extrabold text-amber-400">
                            {contributor.totalPuntos ?? 0} pts
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GamificationPage;
